import { useContext, useState, useEffect, useRef } from 'react'
import { APIContext } from 'context/APIContext'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { GetKinesisProfileType } from 'functions/userUtils'
import { UsersIcon, UserIcon, FlagIcon } from '@heroicons/react/24/outline'
import { TrashIcon } from '@heroicons/react/24/outline'
import kinesisLogo from 'media/kinesis_logo.png'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import SearchBar from 'components/Search/SearchBar'
import PaginationController from 'components/Pagination/PaginationController'

const GetDaysUntilProgramEndsLabel = (program) => {
    if (program.programStatus !== 'in-progress' || !program.endDate) {
        return null
    }

    const endDate = moment(program.endDate, 'YYYY-MM-DD', true)

    if (!endDate.isValid()) {
        return null
    }

    const daysRemaining = endDate.startOf('day').diff(moment().local().startOf('day'), 'days')

    if (daysRemaining < 0) {
        return null
    }

    if (daysRemaining === 0) {
        return 'Ends today'
    }

    if (daysRemaining === 1) {
        return '1 day remaining'
    }

    return `${daysRemaining} days remaining`
}

const defaultPagination = {
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
}

export default function ProgramDirectoryView() {

    const { GetProgramDirectory, DeleteProgram } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const [programs, setPrograms] = useState(null)
    const [programStatusFilter, setProgramStatusFilter] = useState('all')
    const [searchInput, setSearchInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [refreshKey, setRefreshKey] = useState(0)
    const [pagination, setPagination] = useState(defaultPagination)

    const appliedSearchRef = useRef('')

    const LoadProgramDirectory = async (abortController) => {
        setPrograms(null)

        const result = await GetProgramDirectory('instance', pagination.page, pagination.pageSize, searchTerm, programStatusFilter, moment().local().format('YYYY-MM-DD'), { signal: abortController.signal })

        if (abortController.signal.aborted) {
            return
        }

        if (!result) {
            setPrograms([])
            return
        }

        const nextPagination = result.pagination

        if (nextPagination && nextPagination.totalPages > 0 && nextPagination.page > nextPagination.totalPages) {
            setPagination({ ...nextPagination, page: nextPagination.totalPages })
            return
        }

        setPrograms(result.programs ?? [])

        if (nextPagination) {
            setPagination(nextPagination)
        }
    }

    const HandleProgramStatusFilterChange = (value) => {
        setProgramStatusFilter(value)
        setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 })
    }

    const OnProgramClicked = (program) => {
        const kinesisProfileType = GetKinesisProfileType(program.kinesisUserGroupId, program.userId)

        if (kinesisProfileType === 'group') {
            const url = `/group/${program?.kinesisUserGroupId}`
            window.open(url, '_blank')
        }

        if (kinesisProfileType === 'group-individual' || kinesisProfileType === 'individual') {
            const url = `/athlete/${program?.userId}`
            window.open(url, '_blank')
        }
    }

    const ShowDeleteProgramModal = (program) => {
        const modalData = {
            key: ModalKey.DeleteProgram,
            size: 'third',
            programId: program?.programId,
            titleText: 'Delete Program "' + program?.name + '"?',
            bodyText: [
                'All data associated with this program will be deleted.',
                'This action cannot be undone.'
            ],
            buttonText: 'Delete',
            buttonAction: async () => {
                const result = await DeleteProgram(program?.programId)

                if (result) {
                    setRefreshKey(key => key + 1)
                }
            },
        }

        ShowModal(modalData)
    }

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const trimmed = searchInput.trim()

            if (appliedSearchRef.current === trimmed) {
                return
            }

            appliedSearchRef.current = trimmed
            setSearchTerm(trimmed)
            setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 })
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [searchInput])

    useEffect(() => {
        const abortController = new AbortController()
        LoadProgramDirectory(abortController)
        return () => abortController.abort()
    }, [searchTerm, programStatusFilter, pagination.page, pagination.pageSize, refreshKey])

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    Programs
                </h1>
            </div>

            <div className='flex items-center gap-x-4'>
                <div className='flex-grow'>
                    <SearchBar
                        placeholder="Search name or description"
                        searchTerm={searchInput}
                        setSearchTerm={setSearchInput}
                        showFiltersToggle={false}
                    />
                </div>

                <div className='w-[200px] rounded-md border border-gray-200 bg-white shadow-md'>
                    <select
                        value={programStatusFilter}
                        className='w-full cursor-pointer rounded-md border-r-[12px] border-transparent bg-white px-3 py-2 text-sm focus:outline-none'
                        onChange={(event) => HandleProgramStatusFilterChange(event.target.value)}
                    >
                        <option value="all">All Programs</option>
                        <option value="past">Past Programs</option>
                        <option value="in-progress">In Progress Programs</option>
                        <option value="active">Active Programs</option>
                        <option value="stale">Stale Programs</option>
                        <option value="future">Future Programs</option>
                    </select>
                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            {programs === null &&
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            }

            {programs !== null &&
                <ContentFrame className='divide-y divide-gray-200'>
                    {programs.length === 0 &&
                        <div className="text-center text-gray-500 text-sm py-3">No programs found</div>
                    }

                    {programs.length > 0 && programs.map((program, index) => {
                        const daysUntilProgramEndsLabel = GetDaysUntilProgramEndsLabel(program)

                        return (
                            <li
                                key={index}
                                className='w-full flex items-center p-2 gap-x-3 cursor-pointer hover:bg-gray-50 relative'
                                onClick={() => OnProgramClicked(program)}
                            >
                                <div className='pl-1 w-8 flex items-center justify-center'>
                                    <img src={kinesisLogo} alt="Kinesis Logo" className="mt-0.5 h-5 w-5" />
                                </div>

                                <p className="text-sm truncate">
                                    {program.name}
                                </p>

                                {program.kinesisUserGroupId !== null &&
                                    <div className='flex shrink-0 items-center gap-x-1 text-xs text-gray-500 whitespace-nowrap'>
                                        <UsersIcon className='h-4 w-4 text-gray-400' />
                                        {program.kinesisUserGroupName}
                                    </div>
                                }

                                {program.userId !== null &&
                                    <div className='flex shrink-0 items-center gap-x-1 text-xs text-gray-500 whitespace-nowrap'>
                                        <UserIcon className='h-4 w-4 text-gray-400' />
                                        {program.userFirstName} {program.userLastName}
                                    </div>
                                }

                                {program.anchorEventName !== null &&
                                    <div className='flex shrink-0 items-center gap-x-1 text-xs text-gray-500 whitespace-nowrap'>
                                        <FlagIcon className='h-4 w-4 text-gray-400' />
                                        {program.anchorEventName}
                                    </div>
                                }

                                <div className='flex-1'/>

                                {daysUntilProgramEndsLabel &&
                                    <p className='shrink-0 text-xs text-gray-500 font-bold whitespace-nowrap'>
                                        {daysUntilProgramEndsLabel}
                                    </p>
                                }

                                <div className='flex shrink-0 items-center text-xs tabular-nums text-gray-500 whitespace-nowrap'>
                                    {moment(program.startDate).format('MMMM D, YYYY')}
                                    {' - '}
                                    {program.endDate ? moment(program.endDate).format('MMMM D, YYYY') : 'Future'}
                                </div>

                                <button 
                                    className='w-8 h-8 -my-2 flex items-center justify-center'
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        ShowDeleteProgramModal(program)
                                    }}
                                >
                                    <TrashIcon className='w-4 h-4 text-gray-500' />
                                </button>
                            </li>
                        )
                    })}
                </ContentFrame>
            }

            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}
